import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { login as loginService, getMe as getMeService } from '@services/auth/auth.service';

const LOCAL_STORAGE_KEY = '@auth:user';

const retrieveUserFromLocalStorage = () => {
  try {
    const user = localStorage.getItem(LOCAL_STORAGE_KEY);
    return user ? JSON.parse(user) : null;
  } catch (error) {
    console.error('Erro ao recuperar usuário do localStorage', error);
    return null;
  }
}

export const login = createAsyncThunk(
  'auth/login',
  async ({ email, password }, { rejectWithValue }) => {
    try {
      const data = await loginService(email, password);
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const getCredentials = createAsyncThunk(
  'auth/getCredentials',
  async (_, { rejectWithValue }) => {
    try {
      const auth = retrieveUserFromLocalStorage();

      if (!auth) {
        return rejectWithValue('User not found');
      }

      const { credentials, user } = auth;
      return { credentials, user };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const refreshUser = createAsyncThunk(
  'auth/refreshUser',
  async (_, { rejectWithValue }) => {
    try {
      const user = await getMeService();
      return user;
    } catch (error) {
      return rejectWithValue(error?.message || 'Erro ao atualizar dados do usuário');
    }
  },
);

const initialState = {
  isLoading: false,
  isLoggedIn: false,
  tryingLogin: false,
  credentialsChecked: false,
  error: '',
  auth: {
    access_token: '',
    token_type: '',
  },
  user: {
    id: '',
    name: '',
    username: '',
    email: '',
    url_photo: '',
    abilities: [],
    created_at: '',
  },
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: (state) => {
      localStorage.removeItem(LOCAL_STORAGE_KEY);
      Object.assign(state, initialState);
    },
    updateUserProfile: (state, { payload }) => {

      state.user = { ...state.user, ...payload };


      const currentAuth = retrieveUserFromLocalStorage();
      if (currentAuth) {
        const updatedAuth = {
          ...currentAuth,
          user: { ...currentAuth.user, ...payload }
        };
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(updatedAuth));
      }
    }
  },
  extraReducers: builder => {
    builder.addCase(login.pending, (state) => {
      state.isLoading = true;
      state.tryingLogin = true;
      state.error = '';
    });

    builder.addCase(login.fulfilled, (state, { payload }) => {
      const { access_token, token_type, user } = payload;
      const isAdmin = user.abilities.some(a => a.ability === 'admin');

      localStorage.setItem(
        LOCAL_STORAGE_KEY,
        JSON.stringify({ credentials: { access_token, token_type }, user }),
      );

      Object.assign(state, {
        isLoading: false,
        isLoggedIn: true,
        tryingLogin: false,
        credentialsChecked: true,
        error: '',
        auth: { access_token, token_type },
        user: { ...user, isAdmin },
      });
    });

    builder.addCase(login.rejected, (state, { payload }) => {
      Object.assign(state, initialState, { error: payload, isLoading: false });
    });

    builder.addCase(getCredentials.pending, (state) => {
      state.isLoading = true;
    });

    builder.addCase(getCredentials.fulfilled, (state, { payload }) => {
      const isAdmin = payload.user.abilities.some(a => a.ability === 'admin');

      Object.assign(state, {
        isLoading: false,
        isLoggedIn: true,
        credentialsChecked: true,
        auth: payload.credentials,
        user: { ...payload.user, isAdmin },
      });

    });

    builder.addCase(getCredentials.rejected, (state) => {
      Object.assign(state, initialState, { isLoggedIn: false, credentialsChecked: true });
    });

    builder.addCase(refreshUser.fulfilled, (state, { payload }) => {
      const isAdmin = payload.abilities.some(a => a.ability === 'admin');
      state.user = { ...payload, isAdmin };

      const currentAuth = retrieveUserFromLocalStorage();
      if (currentAuth) {
        localStorage.setItem(
          LOCAL_STORAGE_KEY,
          JSON.stringify({ ...currentAuth, user: payload }),
        );
      }
    });
  },
});

export const { logout, updateUserProfile } = authSlice.actions;
export const authReducer = state => state.auth;
export default authSlice.reducer;
